import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, tokenStore, type AuthTokens, type CurrentUser } from '../api/client'

interface AuthContextValue {
  user: CurrentUser | null
  initializing: boolean
  login: (email: string, password: string) => Promise<CurrentUser>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => tokenStore.get()?.user ?? null)
  const [initializing, setInitializing] = useState(!!tokenStore.get())

  useEffect(() => {
    let cancelled = false
    if (tokenStore.get()) {
      api
        .get<CurrentUser>('/v1/auth/me')
        .then((r) => !cancelled && setUser(r.data))
        .catch(() => !cancelled && setUser(null))
        .finally(() => !cancelled && setInitializing(false))
    }
    const onLogout = () => setUser(null)
    window.addEventListener('isg:logout', onLogout)
    return () => {
      cancelled = true
      window.removeEventListener('isg:logout', onLogout)
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<AuthTokens>('/v1/auth/login', { email, password })
    tokenStore.set(data)
    setUser(data.user)
    return data.user
  }, [])

  const logout = useCallback(async () => {
    const tokens = tokenStore.get()
    try {
      if (tokens) await api.post('/v1/auth/logout', { refreshToken: tokens.refreshToken })
    } finally {
      tokenStore.set(null)
      // SuperAdmin tenant override must not leak into the next session.
      localStorage.removeItem('isg.tenantId')
      setUser(null)
    }
  }, [])

  const value = useMemo(() => ({ user, initializing, login, logout }), [user, initializing, login, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
